import { Badge, Box, Heading, Text } from "@chakra-ui/react";
import {
  Area,
  AreaChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatEuro } from "../utils/helperFunctions";

type GroupedData = {
  [month: string]: {
    income: number;
    expense: number;
  };
};

// Dummy-Daten (Zum Testen)
// Du kannst die echte Logik der `useTransactions`-Hook verwenden, wie du es zuvor getan hast.
const generateDummyTransactions = () => {
  const transactions = [];
  const months = [
    "Jan 2023",
    "Feb 2023",
    "Mar 2023",
    "Apr 2023",
    "May 2023",
    "Jun 2023",
    "Jul 2023",
    "Aug 2023",
    "Sep 2023",
    "Oct 2023",
    "Nov 2023",
    "Dec 2023",
  ];

  for (let i = 0; i < 5; i++) {
    const randomMonth = months[Math.floor(Math.random() * months.length)];
    const type = Math.random() > 0.5 ? "income" : "expense";
    const amount = Math.floor(Math.random() * 500) + 50; // Zufälliger Betrag zwischen 50 und 500
    const date = new Date(randomMonth + " 01, 2023").toISOString();

    transactions.push({
      type,
      amount,
      date,
      description: `Transaction ${i}`,
      category: type === "income" ? "Salary" : "Shopping",
    });
  }

  return transactions;
};

const groupTransactionsByMonth = (transactions: any[]) => {
  const groupedData: GroupedData = transactions.reduce(
    (acc, transaction) => {
      const transactionDate = new Date(transaction.date);
      if (isNaN(transactionDate.getTime())) return acc;

      const month = transactionDate.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });

      if (!acc[month]) {
        acc[month] = { income: 0, expense: 0 };
      }

      if (transaction.type === "income") {
        acc[month].income += transaction.amount;
      } else {
        acc[month].expense += transaction.amount;
      }

      return acc;
    },
    {} as Record<string, { income: number; expense: number }>
  );

  return Object.entries(groupedData).map(([month, data]) => ({
    month,
    income: data.income,
    expense: data.expense,
  }));
};

export const YearOverviewChart = () => {
  // Für Testzwecke (verwende den echten Hook für die Daten im echten Projekt)
  /* const { transactions } = useTransactions(); */

  const transactions = generateDummyTransactions();

  const monthlyData = groupTransactionsByMonth(transactions);

  // Höchste Einnahmen und Ausgaben finden
  const highestIncome = monthlyData.reduce(
    (acc, curr) =>
      curr.income > acc.amount
        ? { amount: curr.income, month: curr.month }
        : acc,
    { amount: 0, month: "" }
  );

  const highestExpense = monthlyData.reduce(
    (acc, curr) =>
      curr.expense > acc.amount
        ? { amount: curr.expense, month: curr.month }
        : acc,
    { amount: 0, month: "" }
  );

  return (
    <Box
      mt={6}
      textAlign="center"
      width={{
        base: "390px",
        md: "768px",
        lg: "992px",
        xl: "1200px",
      }}
      height="350px"
    >
      <Heading size="xl" mb={8}>
        Einnahmen und Ausgaben | pro Monat (Liniendiagramm)
      </Heading>
      <Box mb={4}>
        <Badge mr={2} fontSize="sm" bg="#e1e1e1">
          Höchste Einnahme:{" "}
          <Text color="green">{formatEuro(highestIncome.amount)}</Text> -{" "}
          {highestIncome.month}
        </Badge>
        <Badge fontSize="sm" mt={{ base: 4 }} mb={{ base: 4 }} bg="#e1e1e1">
          Höchste Ausgabe:{" "}
          <Text color="red">{formatEuro(highestExpense.amount)}</Text> -{" "}
          {highestExpense.month}
        </Badge>
      </Box>

      <ResponsiveContainer>
        <AreaChart
          width={500}
          height={300}
          data={monthlyData}
          margin={{ top: 20, right: 20, left: 40, bottom: 20 }}
        >
          <XAxis dataKey="month" />
          <YAxis tickFormatter={formatEuro} />
          <Tooltip
            formatter={(value: number) => formatEuro(value)}
            labelStyle={{ fontWeight: "bold" }}
          />
          <Legend />
          <Area
            fill="green"
            fillOpacity="0.3"
            type="monotone"
            dataKey="income"
            stroke="#38A169"
            strokeWidth={2}
            name="Einnahmen"
            dot={true}
            activeDot={{ r: 8 }}
          />
          <Area
            fill="red"
            fillOpacity="0.3"
            type="monotone"
            dataKey="expense"
            stroke="#E53E3E"
            strokeWidth={2}
            name="Ausgaben"
            dot={true}
            activeDot={{ r: 8 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};
